/**
 * Onboarding Profile Draft State Management
 *
 * Manages autosave and resume functionality for onboarding profile form.
 * Uses localStorage for persistent storage across sessions.
 *
 * Features:
 * - Automatic draft save on form changes (debounced)
 * - Draft restore on page reload
 * - 24-hour TTL for drafts (auto-cleanup)
 * - Clear draft after successful submission
 *
 * Flow:
 * 1. User starts filling form → auto-saves draft every second (debounced)
 * 2. User closes tab/browser → draft persists in localStorage
 * 3. User returns → draft auto-restores with toast notification
 * 4. User submits successfully → draft cleared
 */

const DRAFT_KEY = 'supabank_profile_draft';
const DRAFT_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

export interface ProfileDraft {
  type: 'individual' | 'business';
  firstName?: string;
  lastName?: string;
  businessName?: string;
  timestamp: number;
}

/**
 * Save profile draft to localStorage
 */
export function saveDraft(values: Partial<Omit<ProfileDraft, 'timestamp'>>): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    console.warn('[draftState] localStorage not available');
    return;
  }

  try {
    const draft: ProfileDraft = {
      type: values.type || 'individual',
      firstName: values.firstName,
      lastName: values.lastName,
      businessName: values.businessName,
      timestamp: Date.now(),
    };

    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    console.log('[draftState] Saved draft:', { ...draft, timestamp: new Date(draft.timestamp).toISOString() });
  } catch (error) {
    console.error('[draftState] Failed to save draft:', error);
  }
}

/**
 * Load profile draft from localStorage (if valid)
 * Returns null if draft doesn't exist or has expired
 */
export function loadDraft(): ProfileDraft | null {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const stored = localStorage.getItem(DRAFT_KEY);
    if (!stored) {
      return null;
    }

    const draft = JSON.parse(stored) as ProfileDraft;
    const age = Date.now() - draft.timestamp;

    if (age > DRAFT_MAX_AGE) {
      console.log('[draftState] Draft expired (age:', Math.floor(age / 1000 / 60), 'minutes), clearing');
      clearDraft();
      return null;
    }

    console.log('[draftState] Loaded draft:', { ...draft, age: Math.floor(age / 1000), timestamp: new Date(draft.timestamp).toISOString() });
    return draft;
  } catch (error) {
    console.error('[draftState] Failed to load draft:', error);
    return null;
  }
}

/**
 * Clear profile draft from localStorage
 */
export function clearDraft(): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    localStorage.removeItem(DRAFT_KEY);
    console.log('[draftState] Cleared draft');
  } catch (error) {
    console.error('[draftState] Failed to clear draft:', error);
  }
}

/**
 * Check if there's a valid draft (without loading it)
 */
export function hasDraft(): boolean {
  return loadDraft() !== null;
}

/**
 * Get draft age in seconds (for display)
 */
export function getDraftAge(): number | null {
  const draft = loadDraft();
  if (!draft) {
    return null;
  }

  return Math.floor((Date.now() - draft.timestamp) / 1000);
}
